import {
  ArrowLeft,
  User,
  ChevronDown,
  ChevronUp,
  Search,
  Tag,
  Package,
  ExternalLink,
  TrendingUp,
} from "lucide-react";

export default function VisitorDetail({
  visitorId,
  staticId,
  visitorEmail,
  journey,
  topBrands,
  collapsedActions,
  expandedSessions,
  onBack,
  onViewAssistantChats,
  toggleSession,
  toggleActionCollapse,
  getActionKey,
  isCollapsibleAction,
  getActionIcon,
  getActionColor,
  getActionBadgeColor,
  getActionLabel,
  formatTimestamp,
  formatDate,
  onViewSpecificChat,
}) {
  const groupBySession = (journey) => {
    const sessions = {};
    journey.forEach((step) => {
      if (!sessions[step.session_id]) sessions[step.session_id] = [];
      sessions[step.session_id].push(step);
    });
    return sessions;
  };

  const sessions = groupBySession(journey);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* BACK */}
      <button
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6 font-semibold transition"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Visitors List
      </button>

      {/* HEADER */}
      <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  Visitor Journey {staticId ? `(ID: ${staticId})` : ""} {visitorEmail && <span className="ml-2 text-indigo-100 bg-white/20 px-2 py-0.5 rounded text-lg">{visitorEmail}</span>}
                </h2>
                <p className="text-sm text-white text-opacity-80 font-mono">
                  UUID: {visitorId}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="bg-white bg-opacity-20 px-4 py-2 rounded-full">
                <span className="text-white font-semibold">
                  {Object.keys(sessions).length} Session
                  {Object.keys(sessions).length > 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* STATS & BRANDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-6">
          <div className="flex flex-col border-r pr-4">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <Stat label="Total Actions" value={journey.length} />
              <Stat label="Sessions" value={Object.keys(sessions).length} />
              <Stat
                label="Buy Clicks"
                value={
                  journey.filter((s) => s.action === "checkout_redirect").length
                }
              />
            </div>
            {onViewAssistantChats && journey.some(s => s.action === "assistant_interaction") && (
              <button
                onClick={onViewAssistantChats}
                className="w-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-semibold py-2 rounded-lg transition text-sm flex items-center justify-center border border-indigo-200"
              >
                View All AI Assistant Chats
              </button>
            )}
          </div>
          <div className="pl-4">
            <p className="text-sm text-gray-600 mb-2 font-semibold">Most Clicked Brands</p>
            {topBrands && topBrands.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {topBrands.slice(0, 5).map(b => (
                  <span key={b.brand} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                    {b.brand} <span className="bg-green-200 px-1.5 py-0.5 rounded ml-1 text-xs">{b.count}</span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No brand clicks yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* JOURNEY */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {Object.entries(sessions).reverse().map(([sessionId, steps], index) => {
          const totalSessions = Object.keys(sessions).length;
          const displaySessionNum = totalSessions - index;
          return (
            <div key={sessionId} className="mb-6 last:mb-0">
              <button
                onClick={() => toggleSession(sessionId)}
                className="w-full flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-4 rounded-lg transition mb-4"
              >
                <div className="flex items-center">
                  <h4 className="text-lg font-semibold text-gray-900 mr-3">
                    Session {displaySessionNum}
                  </h4>
                  <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                    {steps.length} actions
                  </span>
                  <span className="ml-3 text-sm text-gray-500">
                    {formatDate(steps[0].timestamp)}
                  </span>
                </div>
                {expandedSessions[sessionId] ? <ChevronUp /> : <ChevronDown />}
              </button>

              {expandedSessions[sessionId] && (
                <div className="pl-4">
                  {steps.map((step, idx) => {
                    const actionKey = getActionKey(sessionId, idx);
                    const isCollapsed = collapsedActions[actionKey] ?? true;
                    const canCollapse = isCollapsibleAction(step.action);

                    return (
                      <div key={idx} className="flex mb-6 last:mb-0 relative">
                        <div className="flex flex-col items-center mr-4 relative z-10">
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center ${getActionColor(
                              step.action,
                            )} border-2 bg-white`}
                          >
                            {getActionIcon(step.action)}
                          </div>
                        </div>

                        {idx < steps.length - 1 && (
                          <div className="absolute left-6 top-12 bottom-[-1.5rem] w-0.5 bg-gray-300 z-0 -ml-[1px]" />
                        )}

                        <div
                          className={`flex-1 p-4 rounded-lg border-2 ${getActionColor(
                            step.action,
                          )} relative z-10 bg-white`}
                        >
                          {/* HEADER */}
                          <div className="flex justify-between items-center mb-3">
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-semibold ${getActionBadgeColor(
                                step.action,
                              )}`}
                            >
                              {getActionLabel(step.action)}
                            </span>

                            <div className="flex items-center gap-3">
                              {canCollapse && (
                                <button
                                  onClick={() => toggleActionCollapse(actionKey)}
                                  className="text-sm text-indigo-600 hover:underline flex items-center gap-1"
                                >
                                  {isCollapsed ? "Show" : "Hide"}
                                  {isCollapsed ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronUp className="w-4 h-4" />
                                  )}
                                </button>
                              )}

                              <span className="text-sm text-gray-600">
                                {formatTimestamp(step.timestamp)}
                              </span>
                            </div>
                          </div>

                          {/* DATA */}
                          <ActionContent step={step} isCollapsed={isCollapsed} onViewSpecificChat={onViewSpecificChat} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-sm text-gray-600 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function ActionContent({ step, isCollapsed, onViewSpecificChat }) {
  if (step.action === "search") {
    return (
      <>
        <p>
          Search Query: "{step.data.keyword}"
          {step.data.gender && (
            <span className="text-gray-500 text-sm ml-2">
              ({step.data.gender})
            </span>
          )}
          {step.data.brandsearch_label && (
            <span className="text-gray-500 text-sm ml-2">
              ({step.data.brandsearch_label})
            </span>
          )}
        </p>
        {step.data.location && (
          <p>Location: {step.data.location || "Unknown"}</p>
        )}
      </>
    );
  }

  if (step.action === "category_click") {
    return (
      <>
        <p>Category: {step.data.category}</p>
      </>
    );
  }

  if (step.action === "image_search_buy_click") {
    return (
      <>
        <p className="font-semibold text-gray-800">
          Identified Product:{" "}
          <span className="font-normal">
            {step.data.product_title || "Unknown"}
          </span>
        </p>
        {step.data.brand && (
          <p className="font-semibold text-gray-800 mt-1">
            Brand:{" "}
            <span className="font-normal text-green-700 bg-green-50 px-2 py-0.5 rounded">
              {step.data.brand}
            </span>
          </p>
        )}
        {step.data.product_url && (
          <a
            href={step.data.product_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline block mt-2 break-all hover:text-blue-800 transition"
          >
            {step.data.product_url}
          </a>
        )}
      </>
    );
  }

  if (step.action === "product_click") {
    return (
      <>
        <p className="font-semibold text-gray-800">
          Product:{" "}
          <span className="font-normal">
            {step.data.product?.title ||
              step.data.product_title ||
              step.data.product?.product_id}
          </span>
        </p>
        {(step.data.product?.brand || step.data.brand) && (
          <p className="font-semibold text-gray-800 mt-1">
            Brand:{" "}
            <span className="font-normal text-green-700 bg-green-50 px-2 py-0.5 rounded">
              {step.data.product?.brand || step.data.brand}
            </span>
          </p>
        )}
        {(step.data.product?.image_url || step.data.product_image) && (
          <img
            src={step.data.product?.image_url || step.data.product_image}
            alt="Product"
            className="mt-3 rounded shadow-sm w-24 h-24 object-cover"
          />
        )}
      </>
    );
  }

  if (step.action === "checkout_redirect") {
    return (
      <a
        href={step.data.redirect_url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline"
      >
        {step.data.redirect_url}
      </a>
    );
  }

  if (["new_arrivals_visit", "on_sale_visit", "view_all_visit"].includes(step.action)) {
    return (
      <>
        <p className="font-semibold text-gray-800">
          Brand:{" "}
          <span className="font-normal text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
            {step.data.brand || "Unknown"}
          </span>
        </p>
      </>
    );
  }

  if (step.action === "image_search") {
    return (
      <>
        <p className="text-gray-600 mb-2">Image Search Upload</p>
        {step.data.image_url && (
          <img
            src={step.data.image_url}
            alt="Search Input"
            className="mt-2 rounded-lg border shadow-sm max-w-full h-auto max-h-48 object-cover"
          />
        )}
      </>
    );
  }

  if (step.action === "assistant_results_viewed") {
    return (
      <div className="flex justify-between items-start">
        <div className="flex flex-col gap-2">
          <p className="font-semibold text-gray-800">
            Results Viewed:{" "}
            <span className="font-normal text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
              {step.data.count || 0} products
            </span>
          </p>
        </div>
        {step.data.chat_thread_id && onViewSpecificChat && (
          <button
            onClick={() => onViewSpecificChat(step.data.chat_thread_id)}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded transition-colors ml-4"
          >
            Open Chat Thread &rarr;
          </button>
        )}
      </div>
    );
  }

  if (step.action === "assistant_interaction") {
    return (
      <div className="flex justify-between items-start mt-2 bg-gray-50 p-3 rounded border">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-gray-800">
            User: <span className="font-normal text-gray-600">"{step.data.user_message}"</span>
          </p>
          <p className="text-sm font-semibold text-gray-800">
            AI Response: <span className="font-normal text-gray-600">{step.data.ai_response}</span>
          </p>
          {step.data.ai_search_query?.keyword && (
            <p className="text-xs text-indigo-600 mt-1 font-medium">
              → Executed Search: "{step.data.ai_search_query.keyword}" ({step.data.returned_products || 0} results)
            </p>
          )}
        </div>
        {step.data.chat_thread_id && onViewSpecificChat && (
          <button
            onClick={() => onViewSpecificChat(step.data.chat_thread_id)}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap bg-indigo-100 hover:bg-indigo-200 px-3 py-1 rounded transition-colors ml-4"
          >
            Open Chat Thread &rarr;
          </button>
        )}
      </div>
    );
  }

  return null;
}
